/**
 * Storage-Backed Logger
 *
 * Extends the production-safe logger to write logs to Supabase storage
 * Batches log entries and writes them periodically to reduce API calls
 */

import { supabase } from '@xanectech/supabase-client';
import { createLogger } from '@xanectech/lib';
import {
  LogLevel,
  LOG_LEVEL_NAMES,
  LogBatch,
  LogEntry,
  LoggableData,
} from '@xanectech/types';

const logger = createLogger('Data - storageLogger');

function normalizeLogData(data: unknown): Record<string, unknown> | undefined {
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    return data as Record<string, unknown>;
  }

  return undefined;
}

class StorageLogger {
  private batches: Map<string, LogBatch> = new Map();
  private flushInterval: number = 60000; // 1 minute
  private maxBatchSize: number = 100; // Max entries before force flush
  private flushTimer: number | null = null;
  private enabled: boolean = false;

  constructor() {
    // Start flush timer
    this.startFlushTimer();

    // Flush on page unload
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        void this.flushAllBatches(true);
      });
    }
  }

  /**
   * Enable storage logging for authenticated users only
   */
  async enable(): Promise<void> {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        this.enabled = true;
        createLogger('StorageLogger').info('Storage logging enabled');
      }
    } catch (error) {
      logger.error('Failed to enable storage logging:', error as Error);
    }
  }

  /**
   * Disable storage logging and flush remaining logs
   */
  async disable(): Promise<void> {
    this.enabled = false;
    await this.flushAllBatches(true);
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
  }

  /**
   * Add log entry to batch
   */
  addEntry(
    userId: string,
    component: string,
    level: LogLevel,
    message: string,
    data?: unknown,
  ): void {
    if (!this.enabled) return;

    const batchKey = `${userId}:${component}`;
    let batch = this.batches.get(batchKey);

    if (!batch) {
      batch = {
        userId,
        component,
        entries: [],
        startTime: new Date(),
      };
      this.batches.set(batchKey, batch);
    }

    const normalizedData =
      data && typeof data === 'object' && !Array.isArray(data)
        ? (data as Record<string, unknown>)
        : undefined;

    batch.entries.push({
      timestamp: new Date().toISOString(),
      level,
      component,
      message,
      data: normalizedData,
    });

    // Force flush if batch is too large
    if (batch.entries.length >= this.maxBatchSize) {
      void this.flushBatch(batchKey);
    }
  }

  /**
   * Start automatic flush timer
   */
  private startFlushTimer(): void {
    this.flushTimer = window.setInterval(() => {
      void this.flushAllBatches(false);
    }, this.flushInterval) as unknown as number;
  }

  /**
   * Flush specific batch to storage
   */
  private async flushBatch(batchKey: string): Promise<void> {
    const batch = this.batches.get(batchKey);
    if (!batch || batch.entries.length === 0) return;

    try {
      // Create log file content
      const logContent = JSON.stringify(
        {
          component: batch.component,
          startTime: batch.startTime.toISOString(),
          endTime: new Date().toISOString(),
          entries: batch.entries,
        },
        null,
        2,
      );

      const fileName = `${batch.userId}/${batch.component}_${Date.now()}.json`;
      const blob = new Blob([logContent], { type: 'application/json' });

      // Upload to storage
      const { error } = await supabase.storage
        .from('logs')
        .upload(fileName, blob, {
          contentType: 'application/json',
          upsert: false,
        });

      if (error) {
        logger.error('Failed to upload log file:', error);
        return;
      }

      // Create metadata entry
      const { error: metaError } = await supabase.from('log_metadata').insert({
        user_id: batch.userId,
        log_file_path: fileName,
        log_type: this.getMostSevereLevel(batch.entries as LogEntry[]),
        component: batch.component,
        start_time: batch.startTime.toISOString(),
        end_time: new Date().toISOString(),
        entry_count: batch.entries.length,
        file_size: blob.size,
      });

      if (metaError) {
        logger.error('Failed to create log metadata:', metaError);
      }

      // Clear batch
      this.batches.delete(batchKey);
    } catch (error) {
      logger.error('Error flushing batch:', error as Error);
    }
  }

  /**
   * Flush all batches
   */
  private async flushAllBatches(synchronous: boolean): Promise<void> {
    const batchKeys = Array.from(this.batches.keys());

    if (synchronous) {
      // On page unload, just flush what we can
      // Note: Storage API doesn't support synchronous operations
      // Logs may be lost on page close (acceptable tradeoff)
      for (const key of batchKeys) {
        await this.flushBatch(key).catch((err: unknown) => {
          logger.error('Error flushing batch on unload:', {
            error: err instanceof Error ? err.message : 'Unknown flush error',
          });
        });
      }
      this.batches.clear();
    } else {
      // Async flush
      await Promise.all(batchKeys.map((key) => this.flushBatch(key)));
    }
  }

  /**
   * Get most severe log level from entries
   */
  private getMostSevereLevel(entries: LogEntry[]): string {
    const severity: Record<LogLevel, number> = {
      [LogLevel.DEBUG]: 1,
      [LogLevel.INFO]: 2,
      [LogLevel.WARN]: 3,
      [LogLevel.ERROR]: 4,
    };

    let maxSeverity = 0;
    let maxLevel: LogLevel = LogLevel.DEBUG;

    for (const entry of entries) {
      const level = severity[entry.level] || 0;
      if (level > maxSeverity) {
        maxSeverity = level;
        maxLevel = entry.level;
      }
    }

    // Convert enum to string name for database
    return LOG_LEVEL_NAMES[maxLevel].toLowerCase();
  }

  /**
   * Get batch statistics
   */
  getStats(): {
    batches: number;
    totalEntries: number;
    enabled: boolean;
  } {
    let totalEntries = 0;
    for (const batch of this.batches.values()) {
      totalEntries += batch.entries.length;
    }

    return {
      batches: this.batches.size,
      totalEntries,
      enabled: this.enabled,
    };
  }
}

// Singleton instance
const storageLogger = new StorageLogger();

/**
 * Create a logger that writes to both console and storage
 */
const createStorageLogger = (component: string) => {
  const consoleLogger = createLogger(component);

  return {
    debug: (message: string, data?: unknown) => {
      const normalizedData = normalizeLogData(data);
      consoleLogger.debug(
        message,
        normalizedData as Record<string, LoggableData> | undefined,
      );

      // Write to storage if enabled
      supabase.auth
        .getUser()
        .then(({ data: userData }) => {
          if (userData.user) {
            storageLogger.addEntry(
              userData.user.id as string,
              component,
              LogLevel.DEBUG as LogLevel,
              message,
              normalizedData,
            );
          }
        })
        .catch(() => {});
    },

    info: (message: string, data?: unknown) => {
      const normalizedData = normalizeLogData(data);
      consoleLogger.info(
        message,
        normalizedData as Record<string, LoggableData> | undefined,
      );

      supabase.auth
        .getUser()
        .then(({ data: userData }) => {
          if (userData.user) {
            storageLogger.addEntry(
              userData.user.id as string,
              component,
              LogLevel.INFO as LogLevel,
              message,
              normalizedData,
            );
          }
        })
        .catch(() => {});
    },

    warn: (message: string, data?: unknown) => {
      const normalizedData = normalizeLogData(data);
      consoleLogger.warn(
        message,
        normalizedData as Record<string, LoggableData> | undefined,
      );

      supabase.auth
        .getUser()
        .then(({ data: userData }) => {
          if (userData.user) {
            storageLogger.addEntry(
              userData.user.id as string,
              component,
              LogLevel.WARN as LogLevel,
              message,
              normalizedData,
            );
          }
        })
        .catch(() => {});
    },

    error: (message: string, error?: Error | unknown, data?: unknown) => {
      const errorObj = error instanceof Error ? error : undefined;
      const metadata =
        error instanceof Error ? undefined : normalizeLogData(error);
      consoleLogger.error(
        message,
        errorObj || (metadata as Record<string, LoggableData> | undefined),
      );

      supabase.auth
        .getUser()
        .then(({ data: userData }) => {
          if (userData.user) {
            const errorData =
              error instanceof Error
                ? {
                    name: error.name,
                    message: error.message,
                    stack: error.stack,
                  }
                : error;

            storageLogger.addEntry(
              userData.user.id as string,
              component,
              LogLevel.ERROR as LogLevel,
              message,
              { error: errorData, ...(normalizeLogData(data) || {}) },
            );
          }
        })
        .catch(() => {});
    },

    performance: (operation: string, startTime: number) => {
      const duration = performance.now() - startTime;
      consoleLogger.info(
        `Performance: ${operation}`,
        { duration: Math.round(duration * 100) / 100, unit: 'ms' },
        'performance',
      );
    },

    userAction: (action: string, data?: unknown) => {
      const normalizedData = normalizeLogData(data);
      consoleLogger.info(
        `User Action: ${action}`,
        normalizedData ? { data: normalizedData } : undefined,
      );
    },
  };
};

/**
 * Initialize storage logger when user logs in
 */
const initializeStorageLogger = async (): Promise<void> => {
  await storageLogger.enable();
};

/**
 * Clean up storage logger when user logs out
 */
const cleanupStorageLogger = async (): Promise<void> => {
  await storageLogger.disable();
};

export {
  storageLogger,
  createStorageLogger,
  initializeStorageLogger,
  cleanupStorageLogger,
};
